import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth'
import { auth } from '../../firebase'
import TextField from '../Form/TextField'
import Button from '../Button'
import { toast } from 'react-hot-toast' // ✅ Toast import

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const oobCode = searchParams.get('oobCode')

  const [newPassword, setNewPassword] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [isValidCode, setIsValidCode] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false) // ✅ Loader state

  useEffect(() => {
    if (!oobCode) {
      toast.error('Missing reset code.')
      return
    }

    verifyPasswordResetCode(auth, oobCode)
      .then(() => setIsValidCode(true))
      .catch(() => toast.error('Invalid or expired password reset link.'))
  }, [oobCode])

  const handleReset = async (e) => {
    e.preventDefault()
    setIsLoading(true) // ✅ Start loader

    try {
      await confirmPasswordReset(auth, oobCode, newPassword)
      toast.success('Password has been reset successfully.') // ✅ Success toast
      setShowModal(true) // ✅ Show existing modal
    } catch (err) {
      toast.error(err.message || 'Failed to reset password.') // ✅ Error toast
    } finally {
      setIsLoading(false) // ✅ Stop loader
    }
  }

  const handleLoginRedirect = () => {
    setShowModal(false)
    navigate('/login')
  }

  if (!isValidCode)
    return (
      <div style={{ maxWidth: '400px', margin: 'auto', textAlign: 'center' }}>
        <h2>Password Reset</h2>
        <a href='/forgot-password'>Go to Forgot Password</a>
      </div>
    )

  return (
    <div className='login-container'>
      <div className='login-grid'>
        <div className='login-left login-centered'>
          <div className='login-form'>
            <div className='login-logo'>
              <img
                className='login'
                src='/assets/images/live-alarm.png'
                alt='reset password visual'
              />
            </div>

            <form onSubmit={handleReset}>
              <div className='login-input mb-4'>
                <TextField
                  label='New Password'
                  name='newPassword'
                  type='password'
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete='off'
                  placeholder='New Password'
                />
              </div>

              {error && (
                <p
                  style={{
                    color: 'red',
                    textAlign: 'center',
                    margin: '10px'
                  }}>
                  {error}
                </p>
              )}

              <Button
                type='submit'
                className='login-button'
                disabled={isLoading} // ✅ Disable during request
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}{' '}
                {/* ✅ Loader text */}
              </Button>

              <div className='login-signup'>
                <a
                  href='/login'
                  className='login-forgot'
                  style={{ textDecoration: 'none' }}>
                  <strong>Back to Login</strong>
                </a>
              </div>
            </form>
          </div>
        </div>

        <div className='login-right login-centered login-overflow'>
          <img
            className='login-image'
            src='/assets/images/logo.png'
            alt='reset password visual'
          />
        </div>
      </div>

      {/* Existing Success Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999,
            animation: 'fadeIn 0.3s'
          }}>
          <div
            style={{
              background: '#fff',
              padding: '40px 30px',
              borderRadius: '12px',
              maxWidth: '450px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              animation: 'scaleIn 0.3s'
            }}>
            <h2 style={{ marginBottom: '15px', color: '#333' }}>
              🎉 Congratulations!
            </h2>
            <p style={{ marginBottom: '5px', color: '#555', fontSize: '16px' }}>
              Your Account has been successfully setup.
            </p>
            <p
              style={{ marginBottom: '25px', color: '#555', fontSize: '16px' }}>
              Please login to continue.
            </p>
            <Button onClick={handleLoginRedirect}>Go to Login</Button>
          </div>
        </div>
      )}

      {/* Modal Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes scaleIn {
            from { transform: scale(0.8); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
    </div>
  )
}
